import { useCallback, useEffect, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { useDebounced } from "@/lib/useDebounced";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { type Basis as MarginBasis, type Market } from "./api";
import { Failed, PaneSkeleton, useDiscountWrite } from "./DiscountParts";
import {
  ALL, BASIS_WORD, deleteRate, discountRefusal, DISCOUNTS_COPY as T, fractionPct, funnelNotes, funnelStale, FUNNEL_WORD,
  groupEmptyNote, groupFunnelLine, MARKETS, netPricesQuery, netRowsByProgramme, NO_FUNNEL, noRateSet, partnerGroups,
  partnerPreviewQuery, partnerSummary, partnersQuery, pctFraction, productKey, productOptions, programmeBody, programmeDone,
  programmeDraft, putProgramme, putRate, rateBody, rateDone, rateDraft, rateLabel, rateMarketWord, rateRemovedDone,
  refreshPartners, retireProgramme, variantWord, type Basis as ProgrammeBasis, type NetPrices, type NetProgramme, type NetRow,
  type PartnerDraft, type PartnerGroup, type PartnerPreview, type Partners, type ProductOption, type Programme,
  type ProgrammeDraft, type RateBody, type RateDraft, type RateKey, type RateRow,
} from "./discounts";
import { BASES, basisFromSearch, BASIS_URL, MARKET_LABEL, messageFor, money, pct1, personName, stamp, vatPct } from "./format";
import { NOTE_MAX } from "./strategies";
import { BandCell, BandLegend, HeaderInfo } from "./SignalParts";
import { FormError, ResultLine, SectionHead, type Editor, type Result } from "./StrategyParts";

const P = T.partners;
const NAME_MAX = 120;
// The shared TableHead/TableCell are `whitespace-nowrap`. In this fixed-width table a label, a before → after pair or a message
// line that cannot wrap runs over the next cell (Outcome Audit 3 gaps 1-3), so each one here wraps inside its own cell, and a
// header's info mark drops under its label rather than into the neighbouring cell.
const HEAD_WRAP = "whitespace-normal [&>span]:flex-wrap";
const CELL_WRAP = "whitespace-normal break-words";
// The Add-rate row's pickers: a long label ("Choose a product", "All markets") wraps inside its own cell instead of running into
// MARKET, and inside its own label box (flex-1, min-w-0, a word may break) so it never runs under the chevron (Outcome Audit 4).
const NEW_RATE_TRIGGER = "h-auto min-h-8 w-full gap-0.5 px-1 whitespace-normal [&_svg]:size-3.5 text-left text-xs [&>span]:min-w-0 [&>span]:flex-1 [&>span]:[overflow-wrap:anywhere]";
type PartnerMarket = Market | "All";
type MarketRow = NetRow & { market: Market };
type ProgrammeRead = { market: Market; programme: NetProgramme };
type PreviewChange = (key: string, preview: PartnerPreview | null) => void;
export type DraftEntry = { draft: RateDraft; market: Market | null; editedAt: number };
type ProgrammeActivity = { editedAt: number; preview: PartnerPreview | null };
export type PreviewActivity = ProgrammeActivity & { key: string; market: Market };

let editOrder = 0;
const editedNow = () => Date.now() * 1_000 + (++editOrder % 1_000);
export const saveRateLabel = (failed: boolean) => failed ? "Retry" : P.rate.save;

const programmePreviewKey = (market: Market) => `programme|${market}`;
const rowPreviewKey = (row: MarketRow) => `rate|${row.market}|${row.xero_sku}|${row.marketplace_id}`;

export function previewMatchesDraft(preview: PartnerPreview | undefined, market: Market, basis: MarginBasis, draft: PartnerDraft) {
  if (!preview || preview.market !== market || preview.basis !== basis || preview.draft.kind !== draft.kind) return false;
  if (draft.kind === "default") return preview.draft.kind === "default" && preview.draft.rate === draft.rate && preview.draft.available === draft.available;
  return preview.draft.kind === "rate" && preview.draft.tracking === draft.tracking && preview.draft.variant === draft.variant
    && preview.draft.market === draft.market && preview.draft.rate === draft.rate && preview.draft.available === draft.available;
}

export function programmePreviewStamp(basis: MarginBasis | undefined, draftRate: string, restedRate: string,
  changed: boolean, queries: readonly { data?: PartnerPreview }[]) {
  return `${basis ?? ""}|${draftRate}|${restedRate}|${changed}|${JSON.stringify(queries.map((query) => query.data ?? null))}`;
}

export function programmePreviewOptions(programmeId: number, markets: readonly Market[], basis: MarginBasis,
  draft: PartnerDraft, changed: boolean) {
  return markets.map((market) => partnerPreviewQuery(programmeId, market, basis, draft, changed));
}

export function latestPreview(activities: PreviewActivity[], market: Market) {
  return activities.filter((item) => item.market === market && item.preview !== null)
    .reduce<PreviewActivity | null>((latest, item) => !latest || item.editedAt > latest.editedAt ? item : latest, null)?.preview ?? null;
}

export function previewForActivityRow(activities: PreviewActivity[], ownKey: string | null, row: MarketRow) {
  if (ownKey) return activities.find((item) => item.key === ownKey)?.preview ?? null;
  return activities.filter((item) => item.market === row.market && previewPair(row, item.preview) !== null)
    .sort((a, b) => b.editedAt - a.editedAt)[0]?.preview ?? null;
}

export function updateDraftState(current: Record<string, DraftEntry>, key: string, market: Market | null,
  initial: RateDraft, draft: RateDraft, editedAt: number) {
  if (sameRate(initial, draft)) return closeDraftState(current, key);
  return { ...current, [key]: { draft, market, editedAt } };
}

export function closeDraftState(current: Record<string, DraftEntry>, key: string) {
  if (!(key in current)) return current;
  const next = { ...current }; delete next[key]; return next;
}

export function draftStateAfterSave(current: Record<string, DraftEntry>, key: string, saved: boolean) {
  return saved ? closeDraftState(current, key) : current;
}

export function draftForRow(entry: DraftEntry | undefined, initial: RateDraft) {
  return entry?.draft ?? initial;
}

export function updatePreviews(current: Record<string, PartnerPreview>, key: string, preview: PartnerPreview | null) {
  if (current[key] === preview || (!preview && !(key in current))) return current;
  const next = { ...current };
  delete next[key];
  if (preview) next[key] = preview;
  return next;
}

const shownMarkets = (market: PartnerMarket) => market === "All" ? MARKETS : [market];
const marketFromSearch = (value: unknown): PartnerMarket => value === "UK" || value === "EU" || value === "AE" ? value : "All";
export function heldNetReads(previous: readonly NetPrices[], current: readonly NetPrices[]) {
  const byMarket = new Map(previous.map((read) => [read.market, read]));
  for (const read of current) byMarket.set(read.market, read);
  return MARKETS.flatMap((market) => {
    const read = byMarket.get(market);
    return read ? [read] : [];
  });
}

const programmeReads = (reads: NetPrices[]) => {
  const out: Record<number, ProgrammeRead[]> = {};
  for (const read of reads) for (const programme of read.programmes) (out[programme.id] ??= []).push({ market: read.market, programme });
  return out;
};

/** C1 v1.68 (C1, S8): Distribution Partners presents C2's served net and margin answer; no margin is derived in the browser. */
export function PartnersPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const market = marketFromSearch(search.pp_pmarket);
  const basis = basisFromSearch(search.pp_basis);
  const markets = shownMarkets(market);
  const setSearch = (patch: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...patch }, replace: true });
  const queryClient = useQueryClient();
  const partners = useQuery(partnersQuery());
  const nets = useQueries({ queries: MARKETS.map((m) => netPricesQuery(m, basis)) });
  const heldReads = useRef<NetPrices[]>([]);
  if (heldReads.current.length === 0) {
    const cached = queryClient.getQueryCache().findAll({ queryKey: ["pricing", "discounts", "partners", "net"] })
      .sort((left, right) => left.state.dataUpdatedAt - right.state.dataUpdatedAt)
      .map((query) => query.state.data).filter((read): read is NetPrices => read !== undefined);
    heldReads.current = heldNetReads([], cached);
  }
  const currentReads = nets.map((query) => query.data).filter((read): read is NetPrices => read !== undefined);
  const answered = heldNetReads(heldReads.current, currentReads);
  heldReads.current = answered;
  const [result, setResult] = useState<Result>(null);
  const [creating, setCreating] = useState(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const editor: Editor = { areaName, report: setResult, anchor };
  const head = (withAdd: boolean) => (
    <SectionHead title={P.title} desc={P.desc}>{withAdd && <Button size="sm" onClick={() => setCreating(true)}>{P.add}</Button>}</SectionHead>
  );
  const all = [partners, ...nets];
  const answeredMarkets = new Set(answered.map((read) => read.market));
  const missing = (partners.data === undefined && partners.error ? partners.error : undefined)
    ?? nets.find((query, index) => !answeredMarkets.has(MARKETS[index]) && query.data === undefined && query.error)?.error;
  const error = all.find((q) => q.error)?.error;
  const retry = () => { for (const q of all) void q.refetch(); };
  if ((!partners.data || answered.length < MARKETS.length) && !missing) return <section className="flex flex-col gap-2">{head(false)}<PaneSkeleton /></section>;
  if (missing || !partners.data) return <section className="flex flex-col gap-2">{head(false)}<Failed sentence={`${P.cannotLoad} ${messageFor(missing ?? error, areaName)}`} onRetry={retry} /></section>;

  const data = partners.data;
  const shown = answered.filter((read) => markets.includes(read.market));
  const rows = netRowsByProgramme(shown);
  const allRows = netRowsByProgramme(answered);
  const reads = programmeReads(shown);
  const stale = funnelStale(data.funnel);
  return (
    <section className="flex flex-col gap-3">
      {head(true)}
      {error && <Failed sentence={P.notRefreshed} onRetry={retry} />}
      {stale && <Alert role="note" data-funnel-stale=""><AlertDescription>{stale}</AlertDescription></Alert>}
      <ResultLine result={result} />
      {creating && <ProgrammeForm programme={null} partners={data} editor={editor} onClose={() => setCreating(false)} />}
      <PartnersDataBox rows={Object.values(rows).flat()} />
      <PartnersFilters market={market} basis={basis} onMarket={(next) => setSearch({ pp_pmarket: next === "All" ? undefined : next })}
        onBasis={(next) => setSearch({ pp_basis: BASIS_URL[next] })} />
      {data.programmes.length === 0 && <Card><CardContent className="py-6"><p className="font-medium text-ks-ink">{P.empty}</p></CardContent></Card>}
      {partnerGroups(data).map((group) => <TypeGroup key={group.type ?? "none"} group={group} rows={rows} allRows={allRows}
        reads={reads} markets={markets} basis={basis} partners={data} editor={editor} />)}
      {funnelNotes(data.funnel).map((line) => <p key={line} className="text-sm text-ks-grey-1" data-funnel-note="">{line}</p>)}
      {data.programmes.length > 0 && <p className="text-sm text-ks-grey-1">{P.note}</p>}
      <div ref={setAnchor} data-dialog-anchor="" />
    </section>
  );
}

export function PartnersFilters({ market, basis, onMarket, onBasis }: {
  market: PartnerMarket; basis: MarginBasis; onMarket: (market: PartnerMarket) => void; onBasis: (basis: MarginBasis) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label="Market" className="flex items-center gap-1">
        <span className="text-sm text-ks-grey-1">MARKET</span>
        {(["All", ...MARKETS] as PartnerMarket[]).map((key) => <Button key={key} size="sm" variant={market === key ? "default" : "outline"}
          aria-pressed={market === key} data-nonmutating onClick={() => onMarket(key)}>{key === "AE" ? "UAE" : key}</Button>)}
      </div>
      <TooltipProvider>
        <div role="group" aria-label="Margin basis" className="flex flex-wrap items-center gap-1">
          <span className="text-sm text-ks-grey-1">MARGIN BASIS</span>
          {BASES.map((item) => item.off ? (
            <Tooltip key={item.key}><TooltipTrigger asChild><span tabIndex={0}><Button size="sm" variant="outline" disabled>{item.label}</Button></span></TooltipTrigger>
              <TooltipContent>{item.off}</TooltipContent></Tooltip>
          ) : <Button key={item.key} size="sm" variant={basis === item.key ? "default" : "outline"} aria-pressed={basis === item.key}
            data-nonmutating onClick={() => onBasis(item.key as MarginBasis)}>{item.label}</Button>)}
        </div>
      </TooltipProvider>
      <BandLegend />
    </div>
  );
}

export function PartnersDataBox({ rows }: { rows: MarketRow[] }) {
  const priced = rows.filter((row) => row.net_price !== null);
  const incomplete = priced.some((row) => row.figures == null || row.figures.margins?.gross_pct == null
    || row.figures.margins?.fulfilment_pct == null || row.figures.missing.length > 0);
  if (!incomplete) return null;
  const wholesale = priced.filter((row) => row.figures?.missing.includes("no_wholesale_fulfilment")).length;
  return (
    <Card data-partner-data-box><CardContent className="py-3 text-sm text-ks-ink">
      <span className="font-semibold">Some figures have no data</span>{wholesale > 0 && ` · ${wholesale} case SKUs have no wholesale fulfilment cost`}
    </CardContent></Card>
  );
}

function TypeGroup({ group, rows, allRows, reads, markets, basis, partners, editor }: {
  group: PartnerGroup; rows: Record<number, MarketRow[]>; allRows: Record<number, MarketRow[]>; reads: Record<number, ProgrammeRead[]>;
  markets: Market[]; basis: MarginBasis; partners: Partners; editor: Editor;
}) {
  const line = groupFunnelLine(group);
  const empty = groupEmptyNote(group);
  return (
    <div className="flex flex-col gap-2" data-partner-type={group.type ?? "none"}>
      <div className="flex flex-wrap items-baseline gap-2"><h4 className="text-base font-semibold text-ks-ink">
        {group.type === null ? P.group.noType : FUNNEL_WORD[group.type] ?? group.type}</h4>{line && <span className="text-sm text-ks-grey-1">{line}</span>}</div>
      {group.type === null && <p className="text-sm text-ks-grey-1">{P.group.noTypeHint}</p>}
      {empty && <p className="text-sm text-ks-grey-1" data-type-note="">{empty}</p>}
      {group.programmes.map((programme) => <ProgrammeCard key={programme.id} programme={programme} rows={rows[programme.id] ?? []}
        optionRows={allRows[programme.id] ?? []} reads={reads[programme.id] ?? []} markets={markets} basis={basis} partners={partners} editor={editor} />)}
    </div>
  );
}

export function ProgrammeForm({ programme, partners, editor, onClose, markets = [], marginBasis, onPreview, onDraft }: {
  programme: Programme | null; partners: Partners; editor: Editor; onClose: () => void; markets?: Market[]; marginBasis?: MarginBasis;
  onPreview?: PreviewChange; onDraft?: (markets: Market[], open: boolean, editedAt: number) => void;
}) {
  const [draft, setDraft] = useState<ProgrammeDraft>(programmeDraft(programme));
  const [error, setError] = useState<string | null>(null);
  const write = useDiscountWrite(editor, putProgramme, (answer) => programmeDone(programme, answer), refreshPartners);
  const restedRate = useDebounced(draft.default_rate, 300);
  const previewChecked = programme ? programmeBody({ ...draft, default_rate: restedRate }, programme, partners.programmes) : null;
  const previewRate = previewChecked?.ok ? previewChecked.value.default_rate : null;
  const previewChanged = Boolean(programme && previewRate !== null && previewRate !== programme.default_rate);
  const previewDraft: PartnerDraft = { kind: "default", rate: previewRate ?? "", available: true };
  const previewQueries = useQueries({ queries: programme && marginBasis
    ? programmePreviewOptions(programme.id, markets, marginBasis, previewDraft, previewChanged) : [] });
  const currentChecked = programme ? programmeBody(draft, programme, partners.programmes) : null;
  const currentRate = currentChecked?.ok ? currentChecked.value.default_rate : null;
  const currentDraft: PartnerDraft = { kind: "default", rate: currentRate ?? "", available: true };
  const previewStamp = programmePreviewStamp(marginBasis, draft.default_rate, restedRate, previewChanged, previewQueries);
  useEffect(() => {
    if (!onPreview) return;
    for (const [index, market] of markets.entries()) {
      const answer = previewQueries[index]?.data;
      const current = draft.default_rate === restedRate && previewChanged && currentChecked?.ok
        && marginBasis && previewMatchesDraft(answer, market, marginBasis, currentDraft) ? answer ?? null : null;
      onPreview(programmePreviewKey(market), current);
    }
  // The stamp changes only when a preview answer or the draft's changed state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewStamp, onPreview]);
  const F = P.form;
  const set = (patch: Partial<ProgrammeDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
  };
  const setDefaultRate = (rate: string) => {
    const next = { ...draft, default_rate: rate };
    setDraft(next);
    if (!programme || !onDraft) return;
    const checked = programmeBody(next, programme, partners.programmes);
    const nextRate = checked.ok ? checked.value.default_rate : null;
    // The programme default moves every market. Keep one edit order across them even when the market chip currently shows one.
    onDraft(MARKETS, checked.ok && nextRate !== null && nextRate !== programme.default_rate, editedNow());
  };
  const submit = () => {
    const checked = programmeBody(draft, programme, partners.programmes);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    write.mutate(checked.value, { onSuccess: onClose });
  };
  const id = (field: string) => `pp-programme-${programme?.id ?? "new"}-${field}`;
  return (
    <Card><CardContent className="flex flex-col gap-3 py-4">
      <span className="font-semibold text-ks-ink">{programme ? F.editTitle(programme.name) : F.createTitle}</span>
      <div className="flex flex-wrap items-end gap-3">
        {!programme && <div className="flex flex-col gap-1"><Label htmlFor={id("name")} className="text-xs">{F.name}</Label>
          <Input id={id("name")} className="w-64" maxLength={NAME_MAX} value={draft.name} onChange={(event) => set({ name: event.target.value })} /></div>}
        <div className="flex flex-col gap-1"><Label htmlFor={id("rate")} className="text-xs">{F.defaultRate}</Label>
          <div className="flex items-center gap-1"><Input id={id("rate")} inputMode="decimal" className="w-20" value={draft.default_rate}
            onChange={(event) => setDefaultRate(event.target.value)} /><span className="text-xs text-ks-grey-1">{T.editor.pctOff}</span></div></div>
        <div className="flex flex-col gap-1"><Label className="text-xs">{F.basis}</Label>
          <Select value={draft.basis} onValueChange={(value) => set({ basis: value as ProgrammeBasis })}><SelectTrigger className="w-40" aria-label={F.basis}>
            <SelectValue placeholder={F.pickBasis} /></SelectTrigger><SelectContent>{(["unit", "case"] as ProgrammeBasis[]).map((item) =>
              <SelectItem key={item} value={item}>{BASIS_WORD[item]}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex flex-col gap-1"><Label className="text-xs">{F.funnel}</Label>
          <Select value={draft.funnel_type} onValueChange={(value) => set({ funnel_type: value })}><SelectTrigger className="w-40" aria-label={F.funnel}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={NO_FUNNEL}>{F.noFunnel}</SelectItem>{partners.vocabulary.funnel_type.map((item) =>
              <SelectItem key={item} value={item}>{FUNNEL_WORD[item] ?? item}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <p className="text-xs text-ks-grey-1">{F.defaultHint} {F.base(programme ? programme.base : "shopify")}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1"><Label htmlFor={id("volume")} className="text-xs">{F.volumeNote} ({F.optional})</Label>
          <Input id={id("volume")} className="w-80" maxLength={NOTE_MAX} value={draft.volume_note} onChange={(event) => set({ volume_note: event.target.value })} /></div>
        <div className="flex flex-col gap-1"><Label htmlFor={id("note")} className="text-xs">{F.note} ({F.optional})</Label>
          <Input id={id("note")} className="w-80" maxLength={NOTE_MAX} value={draft.note} onChange={(event) => set({ note: event.target.value })} /></div>
      </div>
      <FormError error={error} />
      {previewQueries.some((query) => query.error) && <p className="text-xs text-ks-grey-1">Preview unavailable</p>}
      <div className="flex gap-2"><Button size="sm" onClick={submit} disabled={write.isPending}>{programme ? F.save : F.create}</Button>
        <Button size="sm" variant="outline" data-nonmutating onClick={onClose}>{F.cancel}</Button></div>
    </CardContent></Card>
  );
}

function ProgrammeCard({ programme, rows, optionRows, reads, markets, basis, partners, editor }: {
  programme: Programme; rows: MarketRow[]; optionRows: MarketRow[]; reads: ProgrammeRead[]; markets: Market[]; basis: MarginBasis; partners: Partners; editor: Editor;
}) {
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, DraftEntry>>({});
  const [previews, setPreviews] = useState<Record<string, PartnerPreview>>({});
  const [programmeActivity, setProgrammeActivity] = useState<Partial<Record<Market, ProgrammeActivity>>>({});
  const onPreview = useCallback<PreviewChange>((key, preview) => setPreviews((current) => updatePreviews(current, key, preview)), []);
  const onProgrammePreview = useCallback<PreviewChange>((key, preview) => {
    const market = key.split("|")[1] as Market;
    setProgrammeActivity((current) => current[market]
      ? { ...current, [market]: { ...current[market]!, preview } }
      : current);
  }, []);
  const onProgrammeDraft = useCallback((changedMarkets: Market[], open: boolean, editedAt: number) => {
    setProgrammeActivity((current) => {
      const next = { ...current };
      for (const market of changedMarkets) {
        if (open) next[market] = { editedAt, preview: null };
        else delete next[market];
      }
      return next;
    });
  }, []);
  const setRowDraft = useCallback((key: string, market: Market | null, initial: RateDraft, draft: RateDraft) => {
    setDrafts((current) => updateDraftState(current, key, market, initial, draft, editedNow()));
    setPreviews((current) => updatePreviews(current, key, null));
  }, []);
  const closeRowDraft = useCallback((key: string) => {
    setDrafts((current) => closeDraftState(current, key));
    setPreviews((current) => updatePreviews(current, key, null));
  }, []);
  const finishRowSave = useCallback((key: string, saved: boolean) => {
    setDrafts((current) => draftStateAfterSave(current, key, saved));
    if (saved) setPreviews((current) => updatePreviews(current, key, null));
  }, []);
  const closeEditor = () => {
    setEditing(false);
    setProgrammeActivity({});
  };
  const retire = useDiscountWrite(editor, retireProgramme, (answer) => P.done.retired(answer.name, answer.rates_retired), refreshPartners, () => setRetiring(false));
  const options = productOptions(optionRows);
  const rateActivities = Object.entries(drafts).flatMap(([key, entry]) => {
    if (entry.market === null) return [];
    const preview = previews[key];
    const current = preview && previewMatchesRateDraft(preview, entry.market, basis, entry.draft) ? preview : null;
    return [{ key, market: entry.market, editedAt: entry.editedAt, preview: current }];
  });
  const activities: PreviewActivity[] = [...rateActivities, ...Object.entries(programmeActivity).flatMap(([market, activity]) => {
    if (!activity) return [];
    const preview = activity.preview?.basis === basis ? activity.preview : null;
    return [{ key: programmePreviewKey(market as Market), market: market as Market, editedAt: activity.editedAt, preview }];
  })];
  const headerPreview = (market: Market) => latestPreview(activities, market);
  const previewForRow = (row: MarketRow) => previewForActivityRow(activities, drafts[rowPreviewKey(row)] ? rowPreviewKey(row) : null, row);
  const summaries = reads.map((read) => headerPreview(read.market)?.summary.after ?? read.programme.summary)
    .filter((item) => item !== undefined);
  const summary = partnerSummary(summaries);
  const bandChanges = markets.reduce((total, market) => total + (headerPreview(market)?.band_change_count ?? 0), 0);
  const resolvedRateIds = new Set(rows.flatMap((row) => row.rate_id === null ? [] : [row.rate_id]));
  const unmatchedRates = programme.rates.filter((rate) => !resolvedRateIds.has(rate.id)
    && (rate.market === ALL || markets.includes(rate.market as Market)));
  return (
    <Card><CardContent className="flex flex-col gap-3 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-ks-ink">{programme.name}</span>
        <Badge variant="secondary">{P.terms(programme.basis, programme.base, programme.default_rate === null ? null : vatPct(programme.default_rate))}</Badge>
        {summary.banded_count > 0 && summary.worst_band && <BandCell figure={`Worst: ${summary.worst_band}`} entry={{ band: summary.worst_band, state: "banded", kpi_id: null }} />}
        {summary.banded_count > 0 && <span className="text-xs text-ks-grey-1">{summary.below_good_count} of {summary.banded_count} SKUs below Good</span>}
        {bandChanges > 0 && <span className="text-xs text-ks-grey-1">{bandChanges} rows change band</span>}
        {programme.rate_count > 0 && <Badge variant="outline">{P.rates(programme.rate_count)}</Badge>}
        {programme.funnel_type && <span className="text-xs text-ks-grey-1">{P.funnel(programme.funnel_type)}</span>}
        <span className="text-xs text-ks-grey-1">{P.setBy(personName(programme.set_by), stamp(programme.set_at), programme.source)}</span>
        <span className="ml-auto flex gap-2"><Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={editing}>{P.edit}</Button>
          <Button size="sm" variant="outline" onClick={() => setRetiring(true)}>{P.retire}</Button></span>
      </div>
      {programme.volume_note && <p className="text-sm text-ks-grey-1">{programme.volume_note}</p>}
      {programme.note && <p className="text-sm text-ks-grey-1">{programme.note}</p>}
      {editing && <ProgrammeForm programme={programme} partners={partners} editor={editor} onClose={closeEditor} markets={markets} marginBasis={basis}
        onPreview={onProgrammePreview} onDraft={onProgrammeDraft} />}
      <Table className="table-fixed min-w-[62rem] lg:min-w-0">
        <TableHeader><TableRow>
          <TableHead className={`w-[14%] ${HEAD_WRAP}`}>PRODUCT · SKU</TableHead><TableHead className={`w-[13%] ${HEAD_WRAP}`}>MARKET</TableHead>
          <TableHead className={`w-[17%] ${HEAD_WRAP}`}>RATE</TableHead><TableHead className={`w-[14%] ${HEAD_WRAP}`}>NET PRICE</TableHead>
          <TableHead className={`w-[16%] ${HEAD_WRAP}`}><HeaderInfo label="FULFILMENT" entries={rows.map((row) => row.bands?.fulfilment ?? null)} /></TableHead>
          <TableHead className={`w-[12%] ${HEAD_WRAP}`}><HeaderInfo label="GROSS" entries={rows.map((row) => row.bands?.gross ?? null)} /></TableHead><TableHead className="w-[14%]" />
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && unmatchedRates.length === 0 && <TableRow><TableCell colSpan={7} className="text-ks-grey-1">{P.noRows}</TableCell></TableRow>}
          {rows.map((row) => <MarginRow key={`${row.market}|${row.xero_sku}|${row.marketplace_id}`} programme={programme} row={row}
            draftEntry={drafts[rowPreviewKey(row)]} preview={previewForRow(row)} basis={basis} editor={editor} onPreview={onPreview}
            onDraft={setRowDraft} onCloseDraft={closeRowDraft} onSaveResult={finishRowSave} />)}
          {unmatchedRates.map((rate) => <UnmatchedRateRow key={rate.id} programme={programme} rate={rate} editor={editor}
            draftEntry={drafts[`saved|${rate.id}`]} onDraft={setRowDraft} onCloseDraft={closeRowDraft} onSaveResult={finishRowSave} />)}
          <NewRateRow programme={programme} options={options} editor={editor} />
        </TableBody>
      </Table>
      {retiring && editor.anchor && <RetireDialog programme={programme} container={editor.anchor} pending={retire.isPending}
        onConfirm={() => retire.mutate(programme.id)} onClose={() => setRetiring(false)} />}
    </CardContent></Card>
  );
}

function previewPair(row: MarketRow, preview: PartnerPreview | null) {
  if (!preview || preview.market !== row.market) return null;
  return preview.rows.find(({ after }) => after.xero_sku === row.xero_sku && after.marketplace_id === row.marketplace_id) ?? null;
}

function rowDraft(programme: Programme, row: MarketRow): RateDraft {
  const own = row.rate_id === null ? null : programme.rates.find((rate) => rate.id === row.rate_id) ?? null;
  if (own) return rateDraft(own);
  return { tracking: row.product, variant: row.variant, market: row.market, rate: row.rate === null ? "" : fractionPct(row.rate), available: row.available, note: row.note ?? "" };
}

function sameRate(a: RateDraft, b: RateDraft) {
  return a.available === b.available && (!a.available || pctFraction(a.rate) === pctFraction(b.rate)) && a.note.trim() === b.note.trim();
}

export function ratePreviewOptions(programme: Programme, market: Market, basis: MarginBasis, initial: RateDraft, draft: RateDraft) {
  const checked = rateBody(draft, programme, false);
  const body = checked.ok ? checked.value : null;
  const previewDraft: PartnerDraft = body
    ? { kind: "rate", tracking: body.tracking, variant: body.variant, market: body.market, rate: body.rate, available: body.available }
    : { kind: "rate", tracking: draft.tracking, variant: draft.variant, market: draft.market, rate: null, available: draft.available };
  return partnerPreviewQuery(programme.id, market, basis, previewDraft, !sameRate(initial, draft) && checked.ok);
}

function previewMatchesRateDraft(preview: PartnerPreview, market: Market, basis: MarginBasis, draft: RateDraft) {
  const current: PartnerDraft = {
    kind: "rate", tracking: draft.tracking, variant: draft.variant, market: draft.market,
    rate: draft.available ? pctFraction(draft.rate) : null, available: draft.available,
  };
  return previewMatchesDraft(preview, market, basis, current);
}

function MarginRow({ programme, row, draftEntry, preview, basis, editor, onPreview, onDraft, onCloseDraft, onSaveResult }: {
  programme: Programme; row: MarketRow; draftEntry?: DraftEntry; preview: PartnerPreview | null; basis: MarginBasis; editor: Editor;
  onPreview: PreviewChange; onDraft: (key: string, market: Market | null, initial: RateDraft, draft: RateDraft) => void;
  onCloseDraft: (key: string) => void; onSaveResult: (key: string, saved: boolean) => void;
}) {
  const initial = rowDraft(programme, row);
  const draft = draftForRow(draftEntry, initial);
  const changed = draftEntry !== undefined;
  const setDraft = (next: RateDraft) => onDraft(rowPreviewKey(row), row.market, initial, next);
  const checked = rateBody(draft, programme, false);
  const body = checked.ok ? checked.value : null;
  const restedDraft = useDebounced(draft, 300);
  const previewQuery = useQuery(ratePreviewOptions(programme, row.market, basis, initial, restedDraft));
  const previewKey = rowPreviewKey(row);
  const currentPreviewDraft: PartnerDraft = body
    ? { kind: "rate", tracking: body.tracking, variant: body.variant, market: body.market, rate: body.rate, available: body.available }
    : { kind: "rate", tracking: draft.tracking, variant: draft.variant, market: draft.market, rate: null, available: draft.available };
  useEffect(() => {
    const current = changed && checked.ok && sameRate(draft, restedDraft)
      && previewMatchesDraft(previewQuery.data, row.market, basis, currentPreviewDraft) ? previewQuery.data ?? null : null;
    onPreview(previewKey, current);
  }, [basis, changed, checked.ok, currentPreviewDraft, draft, onPreview, previewKey, previewQuery.data, restedDraft, row.market]);
  const save = useDiscountWrite(editor, (send: RateBody) => putRate(programme.id, send), (answer, send) => rateDone(programme, send, answer), refreshPartners);
  const remove = useDiscountWrite(editor, (key: RateKey) => deleteRate(programme.id, key), (_answer, key) => rateRemovedDone(programme, key), refreshPartners);
  const own = row.rate_id === null ? null : programme.rates.find((rate) => rate.id === row.rate_id) ?? null;
  const pair = previewPair(row, preview);
  const shown = pair?.after ?? row;
  const undo = () => { onCloseDraft(previewKey); save.reset(); };
  const submit = () => {
    if (body) save.mutate(body, { onSuccess: () => onSaveResult(previewKey, true), onError: () => onSaveResult(previewKey, false) });
  };
  const fieldError = changed && !checked.ok ? checked.error : null;
  return (
    <TableRow data-partner-margin-row={`${row.market}|${row.xero_sku}|${row.marketplace_id}`}>
      <TableCell className="whitespace-normal"><span className="font-medium text-ks-ink">{productKey(row)}</span><div className="text-xs text-ks-grey-1">{row.xero_sku}</div></TableCell>
      <TableCell>{MARKET_LABEL[row.market]}</TableCell>
      <TableCell className={CELL_WRAP}>
        <RateBoxes draft={draft} setDraft={setDraft} label={P.rate.label(programme.name, productKey(row), MARKET_LABEL[row.market])}
          availableLabel={P.rate.availableLabel(programme.name, productKey(row), MARKET_LABEL[row.market])} />
        {changed && <Input className="mt-1 h-7 w-full text-xs" maxLength={NOTE_MAX}
          aria-label={P.rate.noteLabel(programme.name, productKey(row), MARKET_LABEL[row.market])} placeholder={T.editor.notePlaceholder}
          value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />}
        <div className="text-xs text-ks-grey-1">{noRateSet(row) ? <Badge variant="secondary">{P.noRateSet}</Badge> : row.resolved_from === "programme" ? P.fromDefault : P.fromRate}</div>
        {!changed && row.note && <div className="truncate text-xs text-ks-grey-1">{row.note}</div>}
        <FormError error={fieldError} />
        {previewQuery.error && changed && <div className="mt-1 text-xs text-ks-grey-1">Preview unavailable</div>}
        {save.error != null && <div role="alert" className={`mt-1 text-xs ${CELL_WRAP}`}>{discountRefusal(save.error, editor.areaName)}</div>}
      </TableCell>
      <TableCell className={`tabular-nums ${CELL_WRAP}`}><NetPrice row={row} pair={pair} /></TableCell>
      <TableCell className={CELL_WRAP}><MarginCell kind="fulfilment" shown={shown} pair={pair} /></TableCell>
      <TableCell className={CELL_WRAP}><MarginCell kind="gross" shown={shown} pair={pair} /></TableCell>
      <TableCell className="whitespace-normal text-right">
        {changed ? <span className="flex flex-wrap justify-end gap-1"><Button size="sm" className="h-7 px-2" onClick={submit} disabled={save.isPending || !checked.ok}>{saveRateLabel(save.isError)}</Button>
          <Button size="sm" variant="outline" className="h-7 px-2" data-nonmutating onClick={undo} disabled={save.isPending}>Undo</Button></span>
          : own && <span className="flex flex-wrap justify-end gap-1"><Button size="sm" variant="outline"
            onClick={() => remove.mutate({ tracking: own.tracking, variant: own.variant, market: own.market })}
            disabled={remove.isPending}>{P.rate.remove}</Button></span>}
      </TableCell>
    </TableRow>
  );
}

export function NetPrice({ row, pair }: { row: MarketRow; pair: { before: NetRow; after: NetRow } | null }) {
  const value = (item: NetRow, muted = false) => item.net_price === null
    ? <span className="text-sm text-ks-grey-1">{noRateSet(item) ? P.noRateSet : item.not_priced}</span>
    : <span className={muted ? "text-ks-grey-1" : "font-semibold text-ks-ink"}>{money(item.net_price, item.currency)}{item.per === "case" && " /case"}</span>;
  const shown = pair?.after ?? row;
  return <>{pair ? <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">{value(pair.before, true)}<ChangeArrow />{value(pair.after)}</div> : value(row)}
    {shown.net_per_unit != null && <div className="text-xs text-ks-grey-1">{money(shown.net_per_unit, shown.currency)} a unit</div>}</>;
}

function ChangeArrow() {
  return <><span className="sr-only">changes to</span><span aria-hidden="true" className="text-ks-grey-1">→</span></>;
}

function missingTip(row: NetRow) {
  const reason = row.figures?.missing[0];
  if (reason === "no_wholesale_fulfilment") return "No wholesale fulfilment cost for case SKUs";
  return reason ? reason.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase()) : undefined;
}

export function MarginCell({ kind, shown, pair }: { kind: "fulfilment" | "gross"; shown: NetRow; pair: { before: NetRow; after: NetRow } | null }) {
  const figure = (item: NetRow) => kind === "fulfilment" ? item.figures?.margins?.fulfilment_pct ?? null : item.figures?.margins?.gross_pct ?? null;
  const entry = shown.bands?.[kind] ?? null;
  const before = pair ? figure(pair.before) : null;
  const after = figure(shown);
  const beforeBand = pair?.before.bands?.[kind]?.band ?? null;
  const afterBand = shown.bands?.[kind]?.band ?? null;
  const changedBand = pair && beforeBand !== afterBand;
  return (
    <div title={after === null ? missingTip(shown) : undefined}>
      <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">{pair && <><span className="text-sm text-ks-grey-1 tabular-nums">{before === null ? "no data" : pct1(before)}</span>
        <ChangeArrow /></>}<BandCell figure={after === null ? null : pct1(after)} entry={entry} /></div>
      {changedBand && <div className="mt-1 text-xs text-ks-grey-1">{beforeBand ?? "no data"} <ChangeArrow /> {afterBand ?? "no data"}</div>}
    </div>
  );
}

function UnmatchedRateRow({ programme, rate, editor, draftEntry, onDraft, onCloseDraft, onSaveResult }: {
  programme: Programme; rate: RateRow; editor: Editor; draftEntry?: DraftEntry;
  onDraft: (key: string, market: Market | null, initial: RateDraft, draft: RateDraft) => void; onCloseDraft: (key: string) => void;
  onSaveResult: (key: string, saved: boolean) => void;
}) {
  const key = `saved|${rate.id}`;
  const initial = rateDraft(rate);
  const draft = draftEntry?.draft ?? initial;
  const changed = draftEntry !== undefined;
  const checked = rateBody(draft, programme, false);
  const body = checked.ok ? checked.value : null;
  const setDraft = (next: RateDraft) => onDraft(key, null, initial, next);
  const save = useDiscountWrite(editor, (send: RateBody) => putRate(programme.id, send), (answer, send) => rateDone(programme, send, answer), refreshPartners);
  const remove = useDiscountWrite(editor, (send: RateKey) => deleteRate(programme.id, send), (_answer, send) => rateRemovedDone(programme, send), refreshPartners);
  const undo = () => { onCloseDraft(key); save.reset(); };
  const reason = rate.available ? "no priced SKU" : "not available";
  const fieldError = changed && !checked.ok ? checked.error : null;
  return (
    <TableRow data-partner-unpriced-rate={rate.id}>
      <TableCell className="whitespace-normal"><span className="font-medium text-ks-ink">{rateLabel(rate)}</span></TableCell>
      <TableCell>{rateMarketWord(rate.market)}</TableCell>
      <TableCell className={CELL_WRAP}>
        <RateBoxes draft={draft} setDraft={setDraft} label={P.rate.label(programme.name, rateLabel(rate), rateMarketWord(rate.market))}
          availableLabel={P.rate.availableLabel(programme.name, rateLabel(rate), rateMarketWord(rate.market))} />
        {changed && <Input className="mt-1 h-7 w-full text-xs" maxLength={NOTE_MAX}
          aria-label={P.rate.noteLabel(programme.name, rateLabel(rate), rateMarketWord(rate.market))} placeholder={T.editor.notePlaceholder}
          value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />}
        {!changed && rate.note && <div className="truncate text-xs text-ks-grey-1">{rate.note}</div>}
        <FormError error={fieldError} />
        {save.error != null && <div role="alert" className={`mt-1 text-xs ${CELL_WRAP}`}>{discountRefusal(save.error, editor.areaName)}</div>}
      </TableCell>
      {["net", "fulfilment", "gross"].map((cell) => <TableCell key={cell} className="whitespace-normal text-ks-grey-1">{reason}</TableCell>)}
      <TableCell className="whitespace-normal text-right">
        {changed ? <span className="flex flex-wrap justify-end gap-1"><Button size="sm" className="h-7 px-2" onClick={() => body && save.mutate(body, {
          onSuccess: () => onSaveResult(key, true), onError: () => onSaveResult(key, false),
        })}
          disabled={save.isPending || !checked.ok}>{saveRateLabel(save.isError)}</Button>
          <Button size="sm" variant="outline" className="h-7 px-2" data-nonmutating onClick={undo} disabled={save.isPending}>Undo</Button></span>
          : <span className="flex flex-wrap justify-end gap-1"><Button size="sm" variant="outline"
            onClick={() => remove.mutate({ tracking: rate.tracking, variant: rate.variant, market: rate.market })}
            disabled={remove.isPending}>{P.rate.remove}</Button></span>}
      </TableCell>
    </TableRow>
  );
}

function NewRateRow({ programme, options, editor }: { programme: Programme; options: ProductOption[]; editor: Editor }) {
  const [draft, setDraft] = useState<RateDraft>(rateDraft(null));
  const [error, setError] = useState<string | null>(null);
  const add = useDiscountWrite(editor, (body: RateBody) => putRate(programme.id, body), (answer, body) => rateDone(programme, body, answer), refreshPartners);
  const variants = options.find((option) => option.tracking === draft.tracking)?.variants ?? [];
  const submit = () => {
    const checked = rateBody(draft, programme, true);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    add.mutate(checked.value, { onSuccess: () => setDraft(rateDraft(null)) });
  };
  const subject = draft.tracking ? rateLabel(draft) : P.rate.pickProduct;
  return (
    <TableRow data-new-rate-row>
      <TableCell><div className="flex flex-col gap-1">
        <Select value={draft.tracking} onValueChange={(value) => setDraft({ ...draft, tracking: value, variant: ALL })}><SelectTrigger className={NEW_RATE_TRIGGER} aria-label={`${P.rate.product}, ${programme.name}`}>
          <SelectValue placeholder={P.rate.pickProduct} /></SelectTrigger><SelectContent>{options.map((option) => <SelectItem key={option.tracking} value={option.tracking}>{option.tracking}</SelectItem>)}</SelectContent></Select>
        <Select value={draft.variant} onValueChange={(value) => setDraft({ ...draft, variant: value })} disabled={!draft.tracking}><SelectTrigger className={NEW_RATE_TRIGGER} aria-label={`${P.rate.variant}, ${programme.name}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{variantWord(ALL)}</SelectItem>{variants.map((variant) => <SelectItem key={variant} value={variant}>{variant}</SelectItem>)}</SelectContent></Select>
      </div></TableCell>
      <TableCell><Select value={draft.market} onValueChange={(value) => setDraft({ ...draft, market: value })}><SelectTrigger className={NEW_RATE_TRIGGER} aria-label={`${P.rate.market}, ${programme.name}`}><SelectValue /></SelectTrigger>
        <SelectContent>{[ALL, ...MARKETS].map((market) => <SelectItem key={market} value={market}>{rateMarketWord(market)}</SelectItem>)}</SelectContent></Select></TableCell>
      <TableCell><RateBoxes draft={draft} setDraft={setDraft} label={P.rate.label(programme.name, subject, rateMarketWord(draft.market))}
        availableLabel={P.rate.availableLabel(programme.name, subject, rateMarketWord(draft.market))} /><FormError error={error} /></TableCell>
      <TableCell colSpan={3}><Input className="h-7 w-full text-xs" maxLength={NOTE_MAX} aria-label={P.rate.noteLabel(programme.name, subject, rateMarketWord(draft.market))}
        placeholder={T.editor.notePlaceholder} value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /></TableCell>
      <TableCell className="text-right"><Button size="sm" onClick={submit} disabled={add.isPending}>{P.rate.add}</Button></TableCell>
    </TableRow>
  );
}

function RateBoxes({ draft, setDraft, label, availableLabel }: { draft: RateDraft; setDraft: (draft: RateDraft) => void; label: string; availableLabel: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input inputMode="decimal" className="h-7 w-16" aria-label={label} value={draft.available ? draft.rate : ""} disabled={!draft.available}
        onChange={(event) => setDraft({ ...draft, rate: event.target.value })} />
      <span className="text-xs text-ks-grey-1">{T.editor.pctOff}</span>
      <label className="flex items-center gap-1 text-xs text-ks-grey-1"><Checkbox aria-label={availableLabel} checked={!draft.available}
        onCheckedChange={(value) => setDraft({ ...draft, available: value !== true })} />{P.rate.notAvailable}</label>
    </div>
  );
}

function RetireDialog({ programme, container, pending, onConfirm, onClose }: {
  programme: Programme; container: HTMLElement; pending: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}><DialogContent container={container} className="sm:max-w-[520px]">
      <DialogHeader><DialogTitle>{P.retireDialog.title(programme.name)}</DialogTitle><DialogDescription className="text-ks-ink">{P.retireDialog.body(programme.rate_count)}</DialogDescription></DialogHeader>
      <DialogFooter><Button variant="outline" data-nonmutating onClick={onClose}>{P.retireDialog.cancel}</Button>
        <Button variant="destructive" disabled={pending} onClick={onConfirm}>{P.retireDialog.confirm}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
